import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { authApi } from '@wordpuppi/shared';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ThemeToggle } from '@/components/ThemeToggle';

export const Route = createFileRoute('/verify/$token')({
  component: VerifyByParam,
});

function VerifyByParam() {
  const { token } = Route.useParams();
  return <VerifyEmailHandler token={token} />;
}

/**
 * Calls GET /api/auth/verify/{token} and reports success/failure.
 * Shared with the query-string variant (`/verify?token=...`) so the emailed link
 * works whether the backend points it at a path param or a query string.
 */
export function VerifyEmailHandler({ token }: { token: string | undefined }) {
  const [state, setState] = useState<'verifying' | 'success' | 'error'>(
    token ? 'verifying' : 'error',
  );
  const ran = useRef(false);

  useEffect(() => {
    if (!token || ran.current) return;
    ran.current = true;
    authApi
      .verifyEmail(token)
      .then(() => setState('success'))
      .catch(() => setState('error'));
  }, [token]);

  return (
    <div className="relative min-h-screen flex items-center justify-center p-4">
      <div className="absolute top-4 right-4">
        <ThemeToggle />
      </div>
      <Card className="w-full max-w-[400px]">
        <CardHeader>
          <CardTitle className="text-center text-2xl">
            {state === 'verifying' && 'Verifying your email'}
            {state === 'success' && 'Email verified'}
            {state === 'error' && 'Verification failed'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {state === 'verifying' && (
            <div className="flex justify-center py-2">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}

          {state === 'success' && (
            <>
              <p className="text-sm text-muted-foreground text-center">
                Your email address is confirmed. You can now sign in.
              </p>
              <Button asChild className="w-full">
                <Link to="/login">Sign in</Link>
              </Button>
            </>
          )}

          {state === 'error' && (
            <>
              <p className="text-sm text-muted-foreground text-center">
                This verification link is invalid or has expired. You can request a new one.
              </p>
              <Button asChild variant="outline" className="w-full">
                <Link to="/verify-email">Resend verification email</Link>
              </Button>
              <p className="text-center text-sm text-muted-foreground">
                <Link to="/login" className="text-primary underline-offset-4 hover:underline">
                  Back to sign in
                </Link>
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
